// Upstream: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT. Surface from @ahcut/ui/recipes panelClass.

import type { ComponentProps, ElementType, HTMLAttributes } from "react";
import { panelClass } from "@ahcut/ui/recipes";
import { cn } from "@/lib/cn";

export interface CardProps extends ComponentProps<"div"> {
  tone?: "default" | "subtle" | "danger";
  padded?: boolean;
}

export function Card({ tone = "default", padded = true, className, ...rest }: CardProps) {
  // data-slot comes after the spread on purpose: print rules in styles.css key
  // on [data-slot="card"], so a caller must not be able to rename the slot.
  return (
    <div
      {...rest}
      data-slot="card"
      className={cn(panelClass({ padded, tone }), "flex flex-col gap-4", className)}
    />
  );
}

export function CardHeader({ className, ...rest }: ComponentProps<"div">) {
  return <div data-slot="card-header" className={cn("flex flex-col gap-1.5", className)} {...rest} />;
}

export interface CardTitleProps extends HTMLAttributes<HTMLElement> {
  as?: "h2" | "h3" | "p";
}

export function CardTitle({ as = "h2", className, ...rest }: CardTitleProps) {
  const Tag: ElementType = as;
  return <Tag data-slot="card-title" className={cn("text-lg font-semibold text-fg-0", className)} {...rest} />;
}

export function CardDescription({ className, ...rest }: ComponentProps<"p">) {
  return <p data-slot="card-description" className={cn("text-sm text-fg-2", className)} {...rest} />;
}

export function CardContent({ className, ...rest }: ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("flex flex-col gap-3", className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: ComponentProps<"div">) {
  return (
    <div data-slot="card-footer" className={cn("flex flex-wrap items-center gap-3", className)} {...rest} />
  );
}
